import {
  createBaseThemeOptions,
  createUnifiedTheme,
  defaultTypography,
  genPageTheme,
  palettes,
} from '@backstage/theme';
import { TOPBAR_HEIGHT } from '../nav/topBarLayout';

// Monospace face for technical/identifier values (OCIDs, IPs, cost
// figures) — exported for pages to apply directly on those cells. Already
// matches the Rafay Design System's own `--font-mono` token, so no change
// needed here when the rest of the palette below switched over to it.
export const MONO_FONT_FAMILY =
  "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
// Body/UI face — Rafay's own `--font-body` token (Karla). Governs the bulk
// of the app's text (buttons, table cells, nav labels, form fields).
const UI_FONT_FAMILY =
  "'Karla', 'Hanken Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
// Display face — Rafay's own `--font-display` token. The real brand face is
// "BW Modelica", a paid font Rafay's own design system doesn't ship either
// (see its README's "please replace" flag) — substituted with Hanken
// Grotesk there too, so this app matches what the design system actually
// renders today rather than a font nobody here has a license for. Reserved
// for headings/titles (see makeHeaderComponents), not the whole UI — Rafay's
// own CSS makes the same body/display split (`html, body` vs `h1..h5`).
const DISPLAY_FONT_FAMILY =
  "'Hanken Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

// Light theme palette — replicates the Rafay Design System's real brand
// tokens (org default design system; see project/colors_and_type.css and
// project/api/tokens.md there) onto this app's existing layout/component
// structure, in place of the earlier hand-picked "cloud control plane"
// reference palette. Named the same as before so every consuming file
// (TopBarUserMenu, SessionExpiredDialog, scaffolderWizardStyle, BrandIcon,
// StatTile, ...) picks up the new brand without any changes of its own —
// only the values below moved, not the token shape.
const LIGHT_CANVAS_BG = '#F7F9FA'; // --gray-50 / --bg-muted
const LIGHT_SURFACE_BG = '#FFFFFF'; // --rafay-white / --bg
const LIGHT_SURFACE_ALT_BG = '#EEF2F4'; // --gray-100
const LIGHT_BORDER = '#DDE4E8'; // --gray-200 / --border
const LIGHT_BORDER_ALT = '#EEF2F4'; // --gray-100
const LIGHT_TEXT_PRIMARY = '#103541'; // --rafay-ink / --fg
const LIGHT_TEXT_SECONDARY = '#43596A'; // --gray-600 / --fg-muted
const LIGHT_TEXT_MUTED = '#5F7480'; // --gray-500 / --fg-subtle
const LIGHT_TEAL = '#00B29C'; // --rafay-teal / --accent
const LIGHT_TEAL_DARK = '#009484'; // --rafay-teal-600
const LIGHT_TEAL_SOFT_BG = '#ECF8F5'; // --rafay-teal-50
// Rafay's own design system literally defines `--success` as the same hex
// as its teal accent (see tokens.md) — kept as-is here rather than picking
// a separate "safer" green, since the point of this pass is to replicate
// the real tokens and see how that reads, not to quietly correct them.
const LIGHT_STATUS_OK = '#00B29C'; // --success
const LIGHT_STATUS_WARNING = '#E0A200'; // --warning
const LIGHT_STATUS_ERROR = '#D6463B'; // --danger
const LIGHT_STATUS_INFO = '#2A8ACB'; // --info

// The sidebar is the one surface in this theme that's dark — matches Rafay's
// own `--bg-inverse` (ink) surface, with muted/default nav text following
// its documented "white at .65/.9 alpha on dark" rule instead of solid
// hand-picked tints.
const LIGHT_SIDEBAR_BG = '#103541'; // --rafay-ink / --bg-inverse
const LIGHT_SIDEBAR_HOVER_BG = '#1A4756'; // --rafay-ink-700 (one step lighter)
const LIGHT_SIDEBAR_TEXT = 'rgba(255,255,255,0.9)';
const LIGHT_SIDEBAR_MUTED_TEXT = 'rgba(255,255,255,0.65)';
// The sidebar's active-item pill reuses the general accent directly — Rafay
// doesn't define a separate "sidebar active" token the way the previous
// palette's reference design tokens file did.
const LIGHT_SIDEBAR_ACTIVE = '#00B29C';

// A flat, single-color header instead of Backstage's default swirly
// gradient burst, so pages read like a console top bar rather than a
// marketing hero.
const flatHeader = (color: string, fontColor?: string) =>
  genPageTheme({ colors: [color], shape: 'none', options: { fontColor } });

// Backstage's own Header component (Header.esm.js) colors its title,
// subtitle, type kicker, and breadcrumb from `theme.page.fontColor` — not
// `theme.palette.text.*`. That mechanism has been unreliable in this app
// since before this redesign (see InfraHeader.tsx's own comment: the
// built-in theme:app/light and theme:app/dark extensions keep winning over
// this app's own `pageTheme`/`defaultPageTheme` registration even though
// they're disabled in app-config.yaml). The previous version of this
// override never set `color` at all, so titles silently fell back to
// Header's own default — white text, which happened to still be legible on
// the old dark background and only became an invisible-on-light bug now.
// Setting color explicitly here, the same way header/title/subtitle
// already override size/weight/padding, sidesteps that broken mechanism
// entirely instead of trying to fix theme.page upstream.
const makeHeaderComponents = (textColor: string, displayFont: string) => ({
  BackstageHeader: {
    styleOverrides: {
      header: {
        padding: '16px 20px',
        minHeight: 'auto',
        boxShadow: 'none',
      },
      // Reference design's own page title is a compact 15px — this theme's
      // earlier 1.5rem (24px) was a big part of the whole app reading as
      // "zoomed in" relative to the reference next to it. Bumped up
      // slightly from a strict 15px match per direct feedback once both
      // were visible side by side. Page titles use the display face (Rafay's
      // own h1-h5 are all `--font-display`), distinct from the body font
      // the rest of the UI runs on.
      title: {
        fontFamily: displayFont,
        fontSize: '1.125rem',
        fontWeight: 600,
        color: textColor,
      },
      subtitle: {
        fontSize: '0.75rem',
        marginTop: 2,
        color: textColor,
      },
      type: {
        color: textColor,
      },
      breadcrumb: {
        color: textColor,
      },
    },
  },
});

// The scaffolder's step-progress UI (Create VM wizard) is a stock MUI
// Stepper with no app-facing override props — active/completed step circles
// already pick up `primary.main` from MUI v4's own StepIcon defaults, so this
// only polishes spacing/connector/label weight/color, not the underlying
// active/completed logic (MUI's own `Mui-active`/`Mui-completed` modifier
// classes already exist on these nodes regardless of this override).
// Parametrized per-theme: dark and light need different resting/connector
// colors (translucent white vs a solid neutral) even though the shape of
// the override is identical.
const makeStepperComponents = (
  accent: string,
  restLine: string,
  restIcon: string,
  cardBorder: string,
) => ({
  MuiStepConnector: {
    styleOverrides: {
      alternativeLabel: { top: 16 },
      line: { borderColor: restLine, borderTopWidth: 2 },
      // MUI v4 warns (and silently no-ops) if `active`/`completed` are used
      // as top-level styleOverrides keys here — the connector's internal
      // state classes win the specificity fight against that shorthand.
      // Nesting the real `.Mui-active`/`.Mui-completed` selectors under
      // `root` instead, per MUI's own suggested fix, is what actually
      // applies at runtime.
      root: {
        '&.Mui-active': { '& $line': { borderColor: accent } },
        '&.Mui-completed': { '& $line': { borderColor: accent } },
      },
    },
  },
  MuiStepLabel: {
    styleOverrides: {
      label: {
        fontSize: '0.8125rem',
        fontWeight: 600,
        '&$active': { fontWeight: 700 },
      },
    },
  },
  MuiStepIcon: {
    styleOverrides: {
      root: {
        width: 36,
        height: 36,
        color: restIcon,
        '&$active': { color: accent },
        '&$completed': { color: accent },
      },
    },
  },
  // The plain padded div wrapping each step's rendered fields — giving it
  // the same flat-panel treatment (border, rounded corners, background)
  // used everywhere else in the app (StatTile/SectionCard) so the wizard
  // step reads as a distinct card instead of bare form fields floating on
  // the page background.
  BackstageTemplateStepper: {
    styleOverrides: {
      formWrapper: {
        padding: 24,
        marginTop: 16,
        borderRadius: 4,
        border: `1px solid ${cardBorder}`,
      },
    },
  },
});

// Dialog paper is freshly mounted each time a dialog opens (MUI's Modal only
// renders children while `open`), so a CSS `animation` — unlike a
// `transition`, which needs an actual property change to replay — correctly
// re-triggers every open, giving every confirmation/reject dialog in the app
// the same subtle pop-in instead of MUI's default flat fade alone.
const dialogComponents = {
  MuiDialog: {
    styleOverrides: {
      paper: {
        animation: '$dialogPop 220ms cubic-bezier(0.16, 1, 0.3, 1) both',
      },
      '@keyframes dialogPop': {
        from: { opacity: 0, transform: 'scale(0.96)' },
        to: { opacity: 1, transform: 'scale(1)' },
      },
    },
  },
};

// Matches the reference design's compact, sentence-case buttons — MUI v4's
// own Button defaults (ALL CAPS text, ~16px horizontal padding, 0.875rem
// font) read noticeably larger/shoutier than the reference next to it,
// which never uppercases and uses a much tighter `px-3 py-1.5 text-xs`
// (small) / `px-2 py-1 text-[11px]` (extra-small) scale. Flat background
// hover only, no lift/shadow — the reference's own buttons don't have one.
const buttonComponents = {
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        // Rafay's own --radius-md (buttons & inputs use this token).
        borderRadius: 6,
        transition: 'background-color 150ms ease, border-color 150ms ease',
      },
      sizeSmall: {
        padding: '4px 12px',
        fontSize: '0.75rem',
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
    },
  },
};

// Sidebar nav item selection/hover. (A separate attempt at smoothing the
// sidebar's *width* collapse/expand transition was reverted: Backstage
// mounts each item's label instantly on open rather than animating it in,
// so slowing the width transition down just stretched out a clipped-text
// artifact inherent to that component. This override doesn't touch that
// mechanism.)
//
// The `selected` block replaces Backstage's default treatment (a thin
// border-left indicator bar) with a full-bleed filled pill — matching the
// reference design's active nav item — by overriding the same `&$root`-
// nested selectors SidebarItem's own `selected` rule uses (see
// @backstage/core-components' Items.tsx); MUI v4's named-stylesheet
// `overrides` mechanism merges these into that same JSS sheet, so the
// `$root` class reference still resolves. Parametrized per-theme since
// dark/light use different accent colors.
const makeSidebarItemComponents = (activeBg: string, activeColor: string) => ({
  BackstageSidebarItem: {
    styleOverrides: {
      root: {
        // Backstage's own default is a hardcoded 48px — noticeably taller
        // than the reference design's compact ~40px nav rows.
        height: 40,
        // The reference design's active nav item is a flat, full-bleed
        // highlight bar (no rounded corners, no inset margin) — not an
        // inset rounded pill.
        borderRadius: 0,
        margin: 0,
        width: '100%',
        transition: 'background-color 150ms ease, color 150ms ease',
      },
      selected: {
        '&$root': {
          backgroundColor: activeBg,
          borderLeft: 'none',
          color: activeColor,
        },
      },
      // SidebarItem's own default hardcodes a 110px label box (tuned for
      // Backstage's stock font) — Inter runs wider per character at the
      // same size, so labels like "Keep VM Running"/"User Management" were
      // clipping mid-word instead of ellipsizing cleanly.
      label: {
        width: 150,
        fontSize: '0.8125rem',
      },
      // Icons default to 1.5em — the reference design's own icons are a
      // small, consistent size. Shrinking just the icon (not the whole
      // item's font-size, which would also shrink the label) is what
      // actually reads as compact instead of oversized.
      itemIcon: {
        fontSize: '1.05em',
        '& svg': {
          width: '1.1em',
          height: '1.1em',
        },
      },
    },
  },
});

// Backstage's <Page> hard-codes `height: 100vh` on its own root (see
// @backstage/core-components' Page.esm.js), which assumes it starts flush
// at the top of the viewport. modules/topBar.tsx renders our fixed TopBar
// above it and pushes Page down by TOPBAR_HEIGHT via padding-top on a
// wrapper div — without this override, Page's still-100vh box then extends
// that same amount past the actual viewport, and the document grows a
// second, mostly-empty scrollbar alongside Page's own internal
// overflow-y:auto one. Shrinking Page's own height to account for the
// offset makes its one internal scrollbar correct and sufficient again —
// the same category of fix as the CssBaseline note above, just for a
// height mismatch introduced later by the TopBar rather than a leftover
// body margin.
const pageComponents = {
  BackstagePage: {
    styleOverrides: {
      root: {
        height: `calc(100vh - ${TOPBAR_HEIGHT}px)`,
      },
    },
  },
  // Backstage's own default (24px) leaves a much bigger gap between the page
  // title and the content below it than the reference design's compact `p-5`
  // (20px) layout — most visible as a large blank strip under the page
  // header before the first card/table starts.
  BackstageContent: {
    styleOverrides: {
      root: {
        paddingTop: 8,
      },
    },
  },
};

// Light theme — the app's default, primary experience (see theme/index.tsx):
// the Rafay Design System's real brand palette (ink/teal/white, gray-tinted
// surfaces) applied to this same console layout. Every color below is a
// named token from the top of this file rather than an inline hex, so the
// palette stays traceable to one source instead of several
// similar-but-not-quite-matching greys/teals scattered through the file.
const lightPaperComponents = {
  // Backstage's own MuiPaper default elevation shadows are tuned for a
  // white-on-white surface already, but bumped down slightly further here
  // ("very subtle shadows", not "no shadows" — unlike the dark theme, which
  // strips them entirely because paper/background are intentionally the
  // same color there).
  MuiPaper: {
    styleOverrides: {
      elevation1: { boxShadow: '0 1px 2px rgba(21,33,47,0.04)' },
      elevation2: { boxShadow: '0 2px 6px rgba(21,33,47,0.06)' },
      elevation3: { boxShadow: '0 4px 10px rgba(21,33,47,0.07)' },
      elevation4: { boxShadow: '0 6px 14px rgba(21,33,47,0.08)' },
      // Rafay's own --radius-lg (cards & panels use this token).
      rounded: { borderRadius: 10 },
    },
  },
};

const lightTableComponents = {
  // Table header row reads as quiet uppercase labels on the same canvas
  // color as the page — matching the reference design's resource-table
  // header treatment exactly (`bg-[#F7F5F2]`, i.e. LIGHT_CANVAS_BG) rather
  // than a transparent or separately-tinted header background.
  MuiTableCell: {
    styleOverrides: {
      head: {
        color: LIGHT_TEXT_SECONDARY,
        fontSize: '0.6875rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.034em',
        backgroundColor: LIGHT_CANVAS_BG,
        borderBottom: `1px solid ${LIGHT_BORDER}`,
        padding: '8px 12px',
      },
      // MUI's default body-cell padding (16px/16px, more for the first/last
      // column) is what made every table read as noticeably taller-rowed
      // than the reference next to it — `px-3 py-2` there is 12px/8px.
      root: {
        fontSize: '0.75rem',
        padding: '8px 12px',
        borderBottom: `1px solid ${LIGHT_BORDER_ALT}`,
      },
    },
  },
};

export const rafayLightTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    // `createBaseThemeOptions`'s `fontFamily` shorthand mutates a shared,
    // module-level `defaultTypography` singleton in @backstage/theme instead
    // of returning a fresh object, so a full `typography` object (a plain
    // copy, not that shared reference) is used here instead.
    typography: { ...defaultTypography, fontFamily: UI_FONT_FAMILY },
    palette: {
      ...palettes.light,
      // The primary CTA color is the same dark ink as body text and the
      // sidebar — not the teal accent, which is reserved for the sidebar's
      // active-item pill and hover/focus rings (see LIGHT_TEAL usage below).
      // `dark` (contained-button hover/pressed state) is Rafay's own
      // --rafay-ink-900, one step further down the same ramp `main` sits on.
      primary: { main: LIGHT_TEXT_PRIMARY, dark: '#0A242C' },
      secondary: { main: LIGHT_STATUS_ERROR },
      background: {
        default: LIGHT_CANVAS_BG,
        paper: LIGHT_SURFACE_BG,
      },
      text: {
        primary: LIGHT_TEXT_PRIMARY,
        secondary: LIGHT_TEXT_SECONDARY,
        hint: LIGHT_TEXT_MUTED,
      },
      navigation: {
        ...palettes.light.navigation,
        background: LIGHT_SIDEBAR_BG,
        color: LIGHT_SIDEBAR_TEXT,
        selectedColor: '#FFFFFF',
        indicator: LIGHT_TEAL,
        navItem: { hoverBackground: LIGHT_SIDEBAR_HOVER_BG },
        submenu: { background: LIGHT_SIDEBAR_BG },
      },
      status: {
        ...palettes.light.status,
        ok: LIGHT_STATUS_OK,
        running: LIGHT_STATUS_OK,
        warning: LIGHT_STATUS_WARNING,
        pending: LIGHT_STATUS_WARNING,
        error: LIGHT_STATUS_ERROR,
        info: LIGHT_STATUS_INFO,
      },
      border: LIGHT_BORDER,
    },
    defaultPageTheme: 'tool',
    pageTheme: {
      tool: flatHeader(LIGHT_CANVAS_BG, LIGHT_TEXT_PRIMARY),
      home: flatHeader(LIGHT_CANVAS_BG, LIGHT_TEXT_PRIMARY),
    },
  }),
  components: {
    ...makeHeaderComponents(LIGHT_TEXT_PRIMARY, DISPLAY_FONT_FAMILY),
    ...makeStepperComponents(LIGHT_TEAL, LIGHT_BORDER, '#C9C4B6', LIGHT_BORDER),
    ...lightPaperComponents,
    ...lightTableComponents,
    ...dialogComponents,
    ...buttonComponents,
    ...makeSidebarItemComponents(LIGHT_SIDEBAR_ACTIVE, '#FFFFFF'),
    ...pageComponents,
  },
});

// Re-exported so components that need the raw tokens (sign-in page, sidebar
// logo, header scope selectors) can stay in sync with the theme instead of
// hand-copying hex values, the way signInPage.tsx's dark-era constants did.
export const lightTokens = {
  canvasBg: LIGHT_CANVAS_BG,
  surfaceBg: LIGHT_SURFACE_BG,
  surfaceAltBg: LIGHT_SURFACE_ALT_BG,
  border: LIGHT_BORDER,
  borderAlt: LIGHT_BORDER_ALT,
  textPrimary: LIGHT_TEXT_PRIMARY,
  textSecondary: LIGHT_TEXT_SECONDARY,
  textMuted: LIGHT_TEXT_MUTED,
  teal: LIGHT_TEAL,
  tealDark: LIGHT_TEAL_DARK,
  tealSoftBg: LIGHT_TEAL_SOFT_BG,
  statusOk: LIGHT_STATUS_OK,
  statusWarning: LIGHT_STATUS_WARNING,
  statusError: LIGHT_STATUS_ERROR,
  statusInfo: LIGHT_STATUS_INFO,
  sidebarBg: LIGHT_SIDEBAR_BG,
  sidebarHoverBg: LIGHT_SIDEBAR_HOVER_BG,
  sidebarText: LIGHT_SIDEBAR_TEXT,
  sidebarMutedText: LIGHT_SIDEBAR_MUTED_TEXT,
};
